import React from 'react';
import { AlertCircle } from '../icons';
import { PrimaryButton } from './PrimaryButton';
import styles from './ErrorState.module.css';

export interface ErrorStateProps {
  /** Optional icon override. Defaults to lucide AlertCircle. */
  icon?: React.ReactNode;
  /** Short friendly heading — what went wrong, in warm copy. */
  heading: string;
  /** One sentence of body context. */
  body: string;
  /** Primary action — usually "Try again" or similar. */
  action?: {
    label: string;
    onClick: () => void | Promise<void>;
  } | undefined;
  /** Optional secondary action — e.g. "Back to your crates". */
  secondaryAction?: {
    label: string;
    onClick: () => void;
  } | undefined;
}

export function ErrorState({
  icon,
  heading,
  body,
  action,
  secondaryAction,
}: ErrorStateProps): React.ReactElement {
  return (
    <div className={styles.container} role="alert" data-testid="error-state">
      <div className={styles.iconWrap} aria-hidden="true">
        {icon ?? <AlertCircle size={28} />}
      </div>
      <h2 className={styles.heading} data-testid="error-state-heading">
        {heading}
      </h2>
      <p className={styles.body} data-testid="error-state-body">
        {body}
      </p>
      {action !== undefined && (
        <PrimaryButton
          onClick={action.onClick}
          data-testid="error-state-action"
          style={{ marginTop: 'var(--space-2)' }}
        >
          {action.label}
        </PrimaryButton>
      )}
      {secondaryAction !== undefined && (
        <button
          type="button"
          className={styles.secondary}
          onClick={secondaryAction.onClick}
          data-testid="error-state-secondary"
        >
          {secondaryAction.label}
        </button>
      )}
    </div>
  );
}
